import { useEffect, useState } from "react";
import { useHub } from "../../core/Global";
import Icon from "../base/Icon";
import Indicator from "../base/Indicator";
import { ANNUAL_DISCOUNT_PERCENT, Plan } from "./plans";
import "./PlanCatalog.css";


export type PlanBilling = "monthly" | "annual"

interface PlanCatalogProps {
    /** The plan drawn as chosen, by code. */
    value?: string
    onSelect?: (code: string, plan: Plan) => void

    /** Controlled billing period; left out, the catalog keeps its own. */
    billing?: PlanBilling
    onBillingChange?: (billing: PlanBilling) => void

    /**
     * Enterprise belongs on the pricing page, where "Get a Custom Offer" starts a conversation
     * with a representative. It is left out where the catalog has to end in a plan someone can
     * actually start on — nobody can sign up on a price that has not been quoted yet.
     */
    showEnterprise?: boolean

    /** Called once the plans are in, so a chooser can preselect one. */
    onLoaded?: (plans: Plan[]) => void

    /**
     * Leaves out anything with nothing to pay. Set where the catalog is shown to somebody who
     * already has a plan and is moving up from it: Free is not somewhere to upgrade to, and
     * offering it there reads as an option rather than as where they already are.
     */
    paidOnly?: boolean

    /**
     * The plan the customer is already on, and the period they are on it for. Its card is marked
     * as theirs and stops responding, rather than being clickable into an error that only says
     * what they could already see.
     *
     * The period is part of it: on Pro monthly, Pro annual is still somewhere to go.
     */
    currentCode?: string
    currentBilling?: PlanBilling

    /**
     * What they pay now. Anything at or below it cannot be bought here -- moving down means
     * deciding what happens to allowances already spent, which the upgrade path does not answer
     * -- so those cards are dimmed instead of leading to a refusal.
     */
    currentPrice?: number
    // When given, the smaller paid plans are offered as a downgrade instead of being greyed out.
    onDowngrade?: (code: string, plan: Plan) => void
}

const money = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
});


// The plan chooser. One component for both places it appears: the public pricing page and the
// chooser inside the product, so the two can never drift apart on price or wording. The plans
// themselves come from the catalog endpoint rather than from anything written here.
export default function PlanCatalog(props: PlanCatalogProps) {

    const hub = useHub();

    const [plans, plansSet] = useState<Plan[]>([]);
    const [loading, loadingSet] = useState<boolean>(true);

    const [ownBilling, ownBillingSet] = useState<PlanBilling>("monthly");
    const billing = props.billing ?? ownBilling;

    const showEnterprise = props.showEnterprise ?? true;

    useEffect(() => {
        loadingSet(true);
        hub.Get(`/base/public/plans?selfServiceOnly=${showEnterprise ? "false" : "true"}`, res => {
            loadingSet(false);
            if (!res.success || !res.data) return;
            const list: Plan[] = props.paidOnly
                ? (res.data as Plan[]).filter(f => f.requiresPayment)
                : res.data;
            plansSet(list);
            props.onLoaded?.(list);
        });
    }, [showEnterprise, props.paidOnly]);

    const setBilling = (b: PlanBilling) => {
        ownBillingSet(b);
        props.onBillingChange?.(b);
    };

    // Free is $0 either way, and Enterprise is quoted rather than priced.
    const priceOf = (plan: Plan) => {
        if (plan.isCustomPriced) return { amount: plan.priceText ?? "Custom", period: "" };
        if (billing === "annual" && plan.annualPrice > 0) return { amount: money.format(plan.annualPrice), period: "/year" };
        return { amount: money.format(plan.monthlyPrice ?? 0), period: "/month" };
    };

    // The one they are on: same plan, same period.
    const isCurrent = (plan: Plan) =>
        !!props.currentCode && plan.code === props.currentCode
        && (props.currentBilling === undefined || props.currentBilling === billing);

    const amountOf = (plan: Plan) =>
        billing === "annual" && plan.annualPrice > 0 ? plan.annualPrice : (plan.monthlyPrice ?? 0);

    // Priced at or below what they already pay, so there is nothing to sell them.
    const isBelow = (plan: Plan) =>
        props.currentPrice !== undefined && !plan.isCustomPriced
        && amountOf(plan) <= props.currentPrice;

    // A smaller plan the customer can move down to, when the page offers that. Free included:
    // moving to Free is how a paid plan is left, and it runs through the same scheduled change.
    const isDowngrade = (plan: Plan) =>
        !!props.onDowngrade && isBelow(plan) && !isCurrent(plan);

    const isOff = (plan: Plan) => isCurrent(plan) || (isBelow(plan) && !isDowngrade(plan));

    // Changing plan from one they already have: every card then says which way it goes.
    const changing = !!props.currentCode;

    const cardClass = (plan: Plan) => {
        var cls = "plan-card";
        if (plan.darkCard) cls += " plan-card-dark";
        if (plan.highlighted) cls += " plan-card-highlighted";
        if (props.value === plan.code) cls += " plan-card-selected";
        if (isCurrent(plan)) cls += " plan-card-current";
        // A plan to move down to is a real choice, drawn as one -- in its own colour, not
        // greyed out as if it could not be pressed. Only a cheaper plan nobody can move to
        // (no downgrade offered here) is switched off.
        else if (isDowngrade(plan)) cls += " plan-card-downgrade";
        else if (isBelow(plan)) cls += " plan-card-off";
        return cls;
    };

    if (loading) return <div className="py-10"><Indicator /></div>;

    return (
        <div className="plan-catalog">

            <div className="plan-catalog-billing">
                <button type="button"
                    className={"plan-billing-option" + (billing === "monthly" ? " active" : "")}
                    onClick={() => setBilling("monthly")}>
                    Monthly
                </button>
                <button type="button"
                    className={"plan-billing-option" + (billing === "annual" ? " active" : "")}
                    onClick={() => setBilling("annual")}>
                    Annual
                    <span className="plan-billing-save">— {ANNUAL_DISCOUNT_PERCENT}% off</span>
                </button>
            </div>

            <div className="plan-cards">
                {
                    plans.map(plan => {

                        const price = priceOf(plan);
                        const selected = props.value === plan.code;
                        const off = isOff(plan);
                        const choose = () => {
                            if (off || !plan.code) return;
                            if (isDowngrade(plan)) props.onDowngrade?.(plan.code, plan);
                            else props.onSelect?.(plan.code, plan);
                        };

                        return (
                            <div key={plan.code} className={cardClass(plan)} onClick={choose}>

                                {
                                    /* While changing plan, the ribbon says which way each card
                                       goes -- grey for the one they are on, blue for a step up,
                                       red for a step down -- in place of the catalog's "Best
                                       value", which is for somebody choosing a first plan. */
                                    changing ?
                                        (isCurrent(plan) ? <div className="plan-ribbon is-current">Your plan</div>
                                            : isDowngrade(plan) ? <div className="plan-ribbon is-downgrade">Downgrade</div>
                                                : !isBelow(plan) && <div className="plan-ribbon is-upgrade">Upgrade</div>)
                                        : plan.highlighted &&
                                        <div className="plan-ribbon">Best value</div>
                                }

                                {
                                    // The chosen plan is marked by a tick sitting on the card's top
                                    // edge, so the button underneath keeps saying what it does.
                                    selected && !off &&
                                    <div className="plan-check">
                                        <Icon icon="check" type="solid" size={15} />
                                    </div>
                                }

                                <div className="plan-eyebrow">{plan.eyebrow}</div>
                                <div className="plan-name">{plan.name}</div>

                                <div className="plan-price">
                                    <span className="plan-price-amount">{price.amount}</span>
                                    <span className="plan-price-period">{price.period}</span>
                                </div>

                                {/* What the annual figure works out to, so the two periods can be
                                    compared without doing the division in your head. Always
                                    rendered — see .plan-price-note — so the cards keep still
                                    when the billing period is switched. */}
                                <div className="plan-price-note">
                                    {
                                        billing === "annual" && plan.annualPrice > 0
                                            ? `${money.format(Math.round(plan.annualPrice / 12))}/month billed annually`
                                            : ""
                                    }
                                </div>

                                <div className="plan-tagline">{plan.tagline}</div>

                                <button type="button" className="plan-action"
                                    disabled={off}
                                    onClick={e => {
                                        e.stopPropagation();
                                        choose();
                                    }}>
                                    {
                                        isCurrent(plan) ? "Your current plan"
                                            : isDowngrade(plan) ? "Downgrade to " + plan.name
                                                : isBelow(plan) ? "Not an upgrade"
                                                    : plan.actionText
                                    }
                                </button>

                                <ul className="plan-features">
                                    {
                                        (plan.features ?? []).map((f, k) =>
                                            <li key={k} className={f.warn ? "warn" : ""}>
                                                <Icon icon={f.warn ? "circle-exclamation" : "check"} type="solid" size={12} />
                                                <span>{f.text}</span>
                                            </li>
                                        )
                                    }
                                </ul>

                            </div>
                        );
                    })
                }
            </div>

        </div>
    );
}
